import EventEmitter from "@/lib/EventEmitter";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { useAuth } from "@/store/auth";
import { getHasActiveSubscription } from "@/store/auth/selectors";
import { CameraStore, cameraStore, useCamera } from "camera/store/camera";
import { stationStore, useStation } from "camera/store/station";
import EventManager from "../events/EventManager";
import DiffMotionDetector from "./DiffMotionDetector";
import LucasKanadeMotionDetector from "./lucasKanade/LucasKanadeMotionDetector";
import ThreeFrameMotionDetector from "./threeFrame/ThreeFrameMotionDetector";
import { getMotionAlgorithm, onMotionAlgorithmChange, onMotionOverlayChange, type MotionAlgorithm } from "./debugSettings";

export default class MotionDetector {
  private static emitter = new EventEmitter();
  private static get openCvState() {
    return cameraStore().openCvState;
  }
  private static get isEnabled() {
    return stationStore().settings.motionDetectionEnabled;
  }
  private static get isRecording() {
    return cameraStore().isRecording;
  }
  static video: HTMLVideoElement | null = null;

  static state: "idle" | "running" = "idle";
  private static sensitivity = stationStore().settings.motionSensitivity;
  private static currentEventId: string | undefined;

  private static handleChange = (isMotion: boolean) => {
    this.emitter.emit("change", isMotion);
    if (isMotion) {
      this.currentEventId = EventManager.startEvent("MOTION")?.uniqueId;
    } else if (this.currentEventId) {
      EventManager.endEvent(this.currentEventId);
      this.currentEventId = undefined;
    }
  };

  private static implementations = {
    diff: new DiffMotionDetector({ sensitivity: this.sensitivity, onChange: this.handleChange }),
    "lucas-kanade": new LucasKanadeMotionDetector({ sensitivity: this.sensitivity, onChange: this.handleChange }),
    "three-frame": new ThreeFrameMotionDetector({ sensitivity: this.sensitivity, onChange: this.handleChange })
  };
  private static activeImplementation: DiffMotionDetector | LucasKanadeMotionDetector | ThreeFrameMotionDetector | null =
    null;

  private static updateSensitivity = (sensitivity: MotionDetectionSensitivity) => {
    log.motion("sensitivity changed in store:", sensitivity);
    this.sensitivity = sensitivity;
    for (const implementation of Object.values(this.implementations)) implementation.setSensitivity(sensitivity);
  };

  private static updateIsEnabled = (enabled: boolean) => {
    log.motion("isEnabled changed in store:", enabled);
    if (!enabled) {
      this.stop();
    } else if (this.state === "idle") this.start();
  };

  private static onIsRecordingUpdate = (isRecording: boolean) => {
    if (isRecording) this.start();
    else this.stop();
  };

  private static updateDetectionZone = () => {
    const zone = useStation.getState().settings.motionDetectionZone ?? null;
    const isPremium = getHasActiveSubscription(useAuth.getState());
    const effectiveZone = isPremium ? zone : null;
    for (const implementation of Object.values(this.implementations)) implementation.setDetectionZone(effectiveZone);
  };

  private static onAlgorithmChange = (algorithm: MotionAlgorithm) => {
    log.motion("motion algorithm changed in debug settings:", algorithm);
    if (this.state !== "running") return;
    this.stop();
    this.start();
  };

  private static onOverlayChange = (enabled: boolean) => {
    log.motion("motion debug overlay changed in debug settings:", enabled);
    if (this.state !== "running") return;
    this.stop();
    this.start();
  };

  static startListeningForUpdates = () => {
    useCamera.subscribe((store) => store.isRecording, this.onIsRecordingUpdate);
    useStation.subscribe((store) => store.settings.motionSensitivity, this.updateSensitivity);
    useStation.subscribe((store) => store.settings.motionDetectionEnabled, this.updateIsEnabled);
    useStation.subscribe((store) => store.settings.motionDetectionZone, this.updateDetectionZone);
    dataSyncEmitter.on("premium-change", this.updateDetectionZone);
    dataSyncEmitter.on("event-maximum-duration-reached", this.forceRestOnEventMaximumDuration);
    onMotionAlgorithmChange(this.onAlgorithmChange);
    onMotionOverlayChange(this.onOverlayChange);
  };

  static on = this.emitter.on;
  static off = this.emitter.off;

  static start = () => {
    if (this.state === "running") return;
    if (!this.isEnabled || !this.isRecording) {
      log.motion("Not starting MotionDetector, disabled or not recording");
      return;
    }

    if (this.openCvState === "loading") this.waitForReadyState();
    else if (this.video && this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA)
      this.waitForVideoData(this.video);
    else if (this.openCvState === "ready") this.startImplementation();
  };

  static stop = () => {
    this.state = "idle";
    this.activeImplementation?.forceRest();
    this.activeImplementation?.stop();
    this.activeImplementation = null;
  };

  private static startImplementation = () => {
    if (!this.video) {
      log.warn("Couln't start motion detector, source video element is:", this.video);
      return;
    }

    const algorithm = getMotionAlgorithm();
    log.motion("starting motion detector implementation:", algorithm);
    this.state = "running";

    const implementation = this.implementations[algorithm];
    this.activeImplementation = implementation;
    this.updateDetectionZone();
    implementation.setSensitivity(this.sensitivity);
    implementation.start(this.video);
  };

  private static waitForReadyState = () => {
    log.motion("waiting for OpenCV ready state...");
    const unsubscribe = useCamera.subscribe((store: CameraStore) => {
      if (store.openCvState === "ready") {
        unsubscribe();
        this.start();
      }
    });
  };

  private static waitForVideoData = (video: HTMLVideoElement) => {
    log.motion("waiting for video 'loadeddata'...");
    video.addEventListener("loadeddata", () => this.start(), { once: true });
  };

  private static forceRestOnEventMaximumDuration = (eventId: string) => {
    if (eventId === this.currentEventId) this.activeImplementation?.forceRest();
  };
}
